import { dispatch } from '@wordpress/data';
import { PAYMENT_STORE_KEY } from '@woocommerce/block-data';
import { select, subscribe } from '@wordpress/data';
import { defaultHooks } from '@wordpress/hooks';
import { PAYONEER_STORE_KEY } from './index';
import { paymentMethodName } from '../util/woocommerce';

export const setUpBlockCheckoutListeners = () => {
	let currentPaymentMethod;

	const wcPaymentStore = select( PAYMENT_STORE_KEY );
	const paymentStoreCallback = () => {
		const paymentMethod = wcPaymentStore.getActivePaymentMethod();
		if ( currentPaymentMethod !== paymentMethod ) {
			dispatch( PAYONEER_STORE_KEY ).setActivePaymentMethod( paymentMethod );
			currentPaymentMethod = paymentMethod;
		}
	};

	const unsubscribePaymentStore = subscribe( paymentStoreCallback, PAYMENT_STORE_KEY );
	paymentStoreCallback();

	return { unsubscribePaymentStore };
};

export const setupClassicCheckoutListeners = () => {
	const paymentMethodCallback = function () {
		const methodName = paymentMethodName();
		dispatch( PAYONEER_STORE_KEY ).setActivePaymentMethod( methodName );
	};

	jQuery( document.body ).on( 'payment_method_selected', paymentMethodCallback );
	paymentMethodCallback();
	setupComponentObserver();
};

/**
 * Sets up a redux observer that monitors the state of the "activeComponent"
 * and fires an action whenever that component changes.
 *
 * The hook is fired only when the component became stable (no change for a
 * given time) and also fires when selecting a non-Payoneer component, or
 * coming back from non-Payoneer to a Payoneer payment method.
 */
const setupComponentObserver = () => {
	const OBSERVED_PROPERTIES = [ 'key', 'isValid', 'isMounted', 'isReady' ];
	const DELAY_UNTIL_STABLE = 25;
	const MAX_STABILIZE_ATTEMPTS = 10;

	const store = select( PAYONEER_STORE_KEY );
	const knownState = {};
	let waitingTimer = null;

	const log = ( ...args ) => console.log( '[componentObserver]', ...args );

	const initializeState = ( active ) =>
		OBSERVED_PROPERTIES.forEach( prop => knownState[ prop ] = null );

	const hasStateChanged = ( active ) =>
		OBSERVED_PROPERTIES.some( prop => knownState[ prop ] !== active[ prop ] );

	const updateKnownState = ( active ) =>
		OBSERVED_PROPERTIES.forEach( prop => knownState[ prop ] = active[ prop ] );

	const didComponentChange = () => {
		const component = store.getActiveComponent();

		if ( hasStateChanged( component ) ) {
			updateKnownState( component );
			return true;
		}

		return false;
	};

	const waitForStableComponent = ( attempt ) => {
		const didChange = didComponentChange();

		if ( !didChange && 0 === attempt ) {
			return;
		}

		if ( didChange && attempt < MAX_STABILIZE_ATTEMPTS ) {
			waitingTimer = setTimeout( waitForStableComponent, DELAY_UNTIL_STABLE, attempt + 1 );
			return;
		}

		log( 'active payment component changed -', knownState );

		/**
		 * Workaround to unblock Klarna, since it does not fire the onValidationInfo callback.
		 *
		 * When the shop currency is EURO, and the country is non-US (e.g. Austria), then selecting
		 * Klarna causes endless spinner on checkout page. This trick unblocks checkout in 3 seconds
		 * so that customer can select another payment method.
		 *
		 * @todo: remove this workaround when the problem is fixed on the Payoneer side. If there
		 *              was any problem with the payment method, we need an event to do something
		 *              about the problem.
 		 */
		if ( 'klarna' === knownState?.key ) {
			setTimeout(
				() => dispatch( PAYONEER_STORE_KEY ).setIsReady( knownState.key, true ),
				3000
			);
		};

		/**
		 * This action fires when the *observed properties* of the active
		 * payment component changed, and the state is stable (no change
		 * for a given time; important during mounting/initialization).
		 *
		 * When a non-Payoneer component is selected, then all properties
		 * are set to `undefined`. This ensures that the action also fires
		 * once when switching from a Payoneer component to another payment
		 * method.
		 */
		defaultHooks.doAction( 'payoneer.checkout.paymentMethodChanged', { ...knownState } );
		waitingTimer = null;
	};

	const observeActiveComponent = () => {
		if ( waitingTimer !== null ) {
			// Early return if we are already waiting for a stable state.
			return;
		}

		waitForStableComponent( 0 );
	};

	log( 'subscribe to store changes' );
	initializeState();
	const unsubscribe = subscribe( observeActiveComponent, PAYONEER_STORE_KEY );

	return { unsubscribe };
};
